import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { VisaDocuments } from "@/pages/VisaSupport";
import { AdminVerificationDeclarations } from "./AdminVerificationDeclarations";
const err = (e) =>
  typeof e?.response?.data?.detail === "string"
    ? e.response.data.detail
    : "Unable to save. Check the fields and try again.";
const STAGES = [
  "enquiry_received",
  "under_review",
  "information_requested",
  "awaiting_payment",
  "documents_requested",
  "preparing_application",
  "ready_for_customer_review",
  "submitted_to_authority",
  "awaiting_authority_decision",
  "decision_recorded",
  "closed",
];
export function AdminVisaRequests() {
  const [items, setItems] = useState([]),
    [message, setMessage] = useState(""),
    [selected, setSelected] = useState(null),
    [form, setForm] = useState({
      status: "under_review",
      note: "",
      fee: "",
      scope: "",
    }),
    [busy, setBusy] = useState(false);
  const load = async () => {
    setItems((await api.get("/admin/visa-requests")).data);
  };
  useEffect(() => {
    load().catch((e) => setMessage(err(e)));
  }, []);
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMessage("");
    try {
      await api.put(`/admin/visa-requests/${selected.id}`, {
        status: form.status,
        note: form.note,
        ...(form.fee
          ? {
              service_charge_pence: Math.round(Number(form.fee) * 100),
              service_scope: form.scope,
            }
          : {}),
      });
      setSelected(null);
      await load();
      setMessage("Customer progress updated.");
    } catch (e) {
      setMessage(err(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="mt-8">
      <h2 className="text-xl font-semibold">Visa support applications</h2>
      <p className="text-sm text-slate-600 mt-2">
        Review free enquiries, publish an agreed service quotation and record
        customer-visible progress. Published quotations cannot be edited.
        Confirm authority submission only after it has actually happened.
      </p>
      {message && (
        <p role="status" className="mt-4">
          {message}
        </p>
      )}
      <div className="grid gap-4 mt-5">
        {items.map((r) => (
          <article key={r.id} className="card p-5">
            <h3 className="font-semibold">
              {r.visa_title} · {r.name || r.email}
            </h3>
            <p className="text-sm mt-2">
              {r.email} · {r.phone} · {r.nationality} · Resident in{" "}
              {r.country_of_residence}
            </p>
            <p className="text-sm text-slate-600 mt-2">{r.message}</p>
            <p className="text-xs mt-3">
              {r.status.replace(/_/g, " ")} · {r.payment_status}{" "}
              {r.quote && `· Quoted ${money(r.quote.price_pence)}`}
            </p>
            <button
              className="btn btn-ghost mt-3"
              onClick={() => {
                setSelected(r);
                setForm({ status: r.status, note: "", fee: "", scope: "" });
              }}
            >
              Review and update
            </button>
            {selected?.id === r.id && (
              <form className="space-y-3 mt-4" onSubmit={save}>
                <label className="block text-sm">
                  Stage
                  <select
                    className="field mt-1"
                    value={form.status}
                    onChange={(e) =>
                      setForm({ ...form, status: e.target.value })
                    }
                  >
                    {STAGES.map((s) => (
                      <option key={s} value={s}>
                        {s.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  Customer-visible update
                  <textarea
                    className="field mt-1"
                    rows={3}
                    required
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                  />
                </label>
                {!r.quote && form.status === "awaiting_payment" && (
                  <>
                    <label className="block text-sm">
                      Total EuropEdu service charge in GBP
                      <input
                        className="field mt-1"
                        type="number"
                        min="0.01"
                        max="100000"
                        step="0.01"
                        required
                        value={form.fee}
                        onChange={(e) =>
                          setForm({ ...form, fee: e.target.value })
                        }
                      />
                    </label>
                    <label className="block text-sm">
                      Agreed service scope
                      <textarea
                        className="field mt-1"
                        minLength={10}
                        required
                        value={form.scope}
                        onChange={(e) =>
                          setForm({ ...form, scope: e.target.value })
                        }
                      />
                    </label>
                    <p className="text-xs text-slate-500">
                      Government application fees are excluded. Include any
                      applicable tax in your service total.
                    </p>
                  </>
                )}
                <button disabled={busy} className="btn btn-primary">
                  {busy ? "Saving…" : "Save customer update"}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost ml-3"
                  onClick={() => setSelected(null)}
                >
                  Cancel
                </button>
                {r.payment_status === "paid" && (
                  <VisaDocuments requestId={r.id} closed />
                )}
              </form>
            )}
          </article>
        ))}
        {!items.length && !message && (
          <p className="text-sm text-slate-500">
            No visa support requests received yet.
          </p>
        )}
      </div>
    </section>
  );
}
const EMPTY = {
  name: "",
  price: "",
  turnaround: "",
  included: "",
  excluded: "",
  limitations: "",
  categories: "",
  active: true,
};
export function AdminVerificationServices() {
  const [items, setItems] = useState([]),
    [form, setForm] = useState(EMPTY),
    [editing, setEditing] = useState(null),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const load = async () =>
    setItems((await api.get("/admin/verification-services")).data);
  useEffect(() => {
    load().catch((e) => setMessage(err(e)));
  }, []);
  const lines = (s) =>
    s
      .split("\n")
      .map((v) => v.trim())
      .filter(Boolean);
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMessage("");
    try {
      const body = {
        name: form.name,
        price_pence: Math.round(Number(form.price) * 100),
        turnaround: form.turnaround,
        included_checks: lines(form.included),
        excluded_checks: lines(form.excluded),
        limitations: form.limitations,
        document_categories: lines(form.categories),
        active: form.active,
      };
      if (editing)
        await api.put(`/admin/verification-services/${editing}`, body);
      else await api.post("/admin/verification-services", body);
      setEditing(null);
      setForm(EMPTY);
      await load();
      setMessage(
        "Service saved. Active services appear on the Verification page. Existing applications keep their agreed service snapshot.",
      );
    } catch (e) {
      setMessage(err(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="mt-8">
      <h2 className="text-xl font-semibold">Verification service catalogue</h2>
      <p className="text-sm text-slate-600 mt-2">
        Publish actual checks, exclusions, prices and turnaround. A current
        legal declaration must also be available before customers can pay.
      </p>
      <AdminVerificationDeclarations />
      {message && (
        <p role="status" className="mt-4">
          {message}
        </p>
      )}
      <div className="grid gap-4 md:grid-cols-2 mt-5">
        {items.map((s) => (
          <article className="card p-5" key={s.id}>
            <h3 className="font-semibold">{s.name}</h3>
            <p className="text-sm mt-2">
              {money(s.price_pence)} · {s.turnaround} ·{" "}
              {s.is_demo_data || s.is_test_data
                ? "Demo/test service — hidden from customers"
                : s.active === false ? "Hidden" : "Published"}
            </p>
            <button
              className="btn btn-ghost mt-3"
              onClick={() => {
                setEditing(s.id);
                setForm({
                  name: s.name,
                  price: (s.price_pence / 100).toFixed(2),
                  turnaround: s.turnaround,
                  included: (s.included_checks || []).join("\n"),
                  excluded: (s.excluded_checks || []).join("\n"),
                  limitations: s.limitations,
                  categories: (s.document_categories || []).join("\n"),
                  active: s.active !== false,
                });
              }}
            >
              Edit service
            </button>
          </article>
        ))}
      </div>
      <form className="card p-6 mt-5 space-y-4" onSubmit={save}>
        <h3 className="font-semibold">
          {editing ? "Edit service" : "Add verification service"}
        </h3>
        {[
          ["name", "Service name"],
          ["price", "Total service price in GBP"],
          ["turnaround", "Turnaround after complete documents and payment"],
        ].map(([key, title]) => (
          <label key={key} className="block text-sm">
            {title}
            <input
              required
              className="field mt-1"
              type={key === "price" ? "number" : "text"}
              min={key === "price" ? "0.01" : undefined}
              max={key === "price" ? "10000" : undefined}
              step={key === "price" ? "0.01" : undefined}
              value={form[key]}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
            />
          </label>
        ))}
        {[
          ["included", "Included checks (one per line)"],
          ["excluded", "Excluded checks (one per line)"],
          ["limitations", "Service limitations"],
          [
            "categories",
            "Document categories (one per line, e.g. passport or employment_contract)",
          ],
        ].map(([key, title]) => (
          <label key={key} className="block text-sm">
            {title}
            <textarea
              required
              className="field mt-1"
              rows={3}
              value={form[key]}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
            />
          </label>
        ))}
        <label className="flex gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
          />
          Publish on the website
        </label>
        <button disabled={busy} className="btn btn-primary">
          {busy ? "Saving…" : "Save service"}
        </button>
        {editing && (
          <button
            type="button"
            className="btn btn-ghost ml-3"
            onClick={() => {
              setEditing(null);
              setForm(EMPTY);
            }}
          >
            Cancel editing
          </button>
        )}
      </form>
    </section>
  );
}
